<template>
  <div v-if="show" class="receipt-overlay" @click.self="$emit('close')">
    <div class="receipt-wrapper">

      <!-- Tugmalar (chop qilishda yashiriladi) -->
      <div class="receipt-actions no-print">
        <button class="btn-print" @click="printReceipt">
          <b-icon icon="printer-fill"></b-icon> Brauzer (A4/PDF)
        </button>
        <button
          class="btn-print-remote"
          :disabled="remotePrint.loading || !saleId"
          :title="!saleId ? 'Avval savdoni saqlang' : 'Lokal chek printerga yuborish'"
          @click="sendToPrinterQueue()"
        >
          <b-icon icon="wifi"></b-icon>
          {{ remotePrint.loading ? 'Yuborilmoqda...' : '🖨 Avtomatik chek (Printerga)' }}
        </button>
        <button class="btn-close-modal" @click="$emit('close')">
          <b-icon icon="x-lg"></b-icon> Yopish
        </button>
      </div>

      <!-- Yuborish natijasi (statusi) -->
      <div v-if="remotePrint.message" class="remote-print-banner no-print"
           :class="remotePrint.success ? 'banner-ok' : 'banner-err'">
        <b-icon :icon="remotePrint.success ? 'check-circle-fill' : 'exclamation-triangle-fill'"></b-icon>
        {{ remotePrint.message }}
        <button v-if="remotePrint.jobId" class="banner-link" @click="checkJobStatus()">
          Holatni tekshir
        </button>
      </div>

      <!-- CHek -->
      <div class="receipt" id="sale-receipt">

        <!-- Logo + Firma header -->
        <div class="receipt-header">
          <img v-if="logoUrl" :src="logoUrl" class="receipt-logo" alt="logo" />
          <div class="firm-name">{{ firmName }}</div>
          <div v-if="phone" class="firm-phone">Kontaktlar: {{ phone }}</div>
          <div class="receipt-date">{{ receiptDate }}</div>
        </div>

        <div class="receipt-divider"></div>

        <!-- Mijoz, Usta, Mashina -->
        <div class="receipt-info">
          <div class="info-row">
            <span class="info-label">Mijoz:</span>
            <span class="info-value">{{ clientName }}</span>
          </div>
          <div v-if="masterName" class="info-row">
            <span class="info-label">Usta:</span>
            <span class="info-value">{{ masterName }}</span>
          </div>
          <div v-if="carNumber" class="info-row">
            <span class="info-label">Mashina:</span>
            <span class="info-value">{{ carNumber }}</span>
          </div>
        </div>

        <div class="receipt-divider"></div>

        <!-- Mahsulotlar ro'yxati (har mahsulot 2 qator) -->
        <div class="items-section">
          <div v-for="(item, i) in receiptItems" :key="i" class="item-block">
            <div class="ir-name">{{ i + 1 }}. {{ item.name }}</div>
            <div v-if="item.altPrice > 0" class="ir-alt">
              Chakana narxi: {{ fmtUZS(item.altPrice) }}
            </div>
            <div v-if="item.origPrice > 0" class="leader-line ll-strike">
              <span class="ll-left">{{ item.qty }} шт x {{ fmt(item.origPrice) }}</span>
              <span class="ll-dots"></span>
              <span class="ll-right">{{ fmtUZS(item.qty * item.origPrice) }}</span>
            </div>
            <div v-if="item.discountPct > 0" class="ir-disc">
              −{{ item.discountPct }}% chegirma
              <template v-if="item.origPrice > 0"> · tejaldi: <b>{{ fmtUZS((item.origPrice - item.unitPrice) * item.qty) }}</b></template>
            </div>
            <div class="leader-line">
              <span class="ll-left">{{ item.qty }} шт x {{ fmt(item.unitPrice) }}</span>
              <span class="ll-dots"></span>
              <span class="ll-right">{{ fmtUZS(item.total) }}</span>
            </div>
          </div>
        </div>

        <div class="receipt-divider"></div>

        <!-- Hisob-kitob -->
        <div class="calc-section">
          <div class="leader-line">
            <span class="ll-left">Mahsulotlarning umumiy soni</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ totalQty }} birlik.</span>
          </div>

          <!-- Narx tushirilgan bolsa: asl jami + chegirma -->
          <div v-if="priceReductionTotal > 0" class="leader-line ll-strike">
            <span class="ll-left">Asl jami (narx tushirilmaganda)</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(originalTotalSum) }}</span>
          </div>
          <div v-if="priceReductionTotal > 0" class="leader-line">
            <span class="ll-left">Narx chegirmasi</span>
            <span class="ll-dots"></span>
            <span class="ll-right">- {{ fmtUZS(priceReductionTotal) }}</span>
          </div>

          <div v-if="skidka > 0" class="leader-line">
            <span class="ll-left">Oraliq jami</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(totalSum + skidka) }}</span>
          </div>
          <div v-if="skidka > 0" class="leader-line">
            <span class="ll-left">Chegirma</span>
            <span class="ll-dots"></span>
            <span class="ll-right">- {{ fmtUZS(skidka) }}</span>
          </div>

          <div class="leader-line ll-jami">
            <span class="ll-left">JAMI</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(totalSum) }}</span>
          </div>

          <!-- To'landi — har pay_type alohida (mobile chek formatida) -->
          <template v-if="visiblePayments.length > 0">
            <div v-for="p in visiblePayments" :key="p.pay_type_id" class="leader-line">
              <span class="ll-left">To'landi ({{ p.pay_type_name }})</span>
              <span class="ll-dots"></span>
              <span class="ll-right">
                <template v-if="p.is_dollar">${{ fmt(p.summa) }}</template>
                <template v-else>{{ fmt(p.summa) }} UZS</template>
              </span>
            </div>
          </template>
          <div v-else-if="paidSumma > 0" class="leader-line">
            <span class="ll-left">To'landi</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(paidSumma) }}</span>
          </div>
          <div v-if="totalDebt > 0" class="leader-line ll-italic">
            <span class="ll-left">Qarzga</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(totalDebt) }}</span>
          </div>
        </div>

        <!-- Mijoz qarzi bloki -->
        <div v-if="previousDebt > 0 || totalDebt > 0" class="debt-section">
          <div class="debt-title">Mijoz qarzi</div>
          <div class="leader-line">
            <span class="ll-left">Xariddan oldingi qarz</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(previousDebt) }}</span>
          </div>
          <div class="leader-line">
            <span class="ll-left">Qarzga qo'shildi</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(totalDebt) }}</span>
          </div>
          <div class="leader-line ll-bold">
            <span class="ll-left">Xariddan keyingi qarz</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(previousDebt + totalDebt) }}</span>
          </div>
          <div v-if="paidFromDebt > 0" class="leader-line">
            <span class="ll-left">Qarzdan yechib olingan</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtUZS(paidFromDebt) }}</span>
          </div>
          <div v-if="debtDueDate > 0" class="leader-line ll-bold">
            <span class="ll-left">To'lov muddati</span>
            <span class="ll-dots"></span>
            <span class="ll-right">{{ fmtDate(debtDueDate) }}</span>
          </div>
          <div v-if="debtComment" class="debt-comment-line">
            Izoh: {{ debtComment }}
          </div>
        </div>

        <div class="receipt-divider"></div>

        <!-- Footer -->
        <div class="receipt-footer">
          <div v-if="checkWord" class="check-word">{{ checkWord }}</div>
        </div>

        <!-- Barcode (mobil ilovadagi usta scanner orqali izlash uchun) -->
        <div v-if="barcodeValue" class="receipt-barcode">
          <barcode :value="barcodeValue" :options="barcodeOptions" tag="svg"></barcode>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import VueBarcode from 'vue-barcode';
import axios from 'axios';

export default {
  name: 'SaleReceiptModal',
  components: { barcode: VueBarcode },
  data() {
    return {
      remotePrint: {
        loading: false,
        success: false,
        message: '',
        jobId: null,
      },
    };
  },
  watch: {
    show(v) { if (v) this.remotePrint = { loading: false, success: false, message: '', jobId: null }; },
  },
  props: {
    show:        { type: Boolean, default: false },
    firmName:    { type: String,  default: '' },
    phone:       { type: String,  default: '' },
    checkWord:   { type: String,  default: '' },
    logoUrl:     { type: String,  default: '' },
    clientName:  { type: String,  default: "NOMA'LUM" },
    masterName:  { type: String,  default: '' },
    carNumber:   { type: String,  default: '' },
    cart:        { type: Array,   default: () => [] },
    totalSum:    { type: Number,  default: 0 },
    skidka:      { type: Number,  default: 0 },
    paidSumma:   { type: Number,  default: 0 },
    previousDebt:{ type: Number,  default: 0 },
    paidFromDebt:{ type: Number,  default: 0 },
    priceType:   { type: String,  default: 'chakana' },
    debtDueDate: { type: Number,  default: 0 },     // unix timestamp
    debtComment: { type: String,  default: '' },
    saleId:      { type: Number,  default: 0 },
    saleCreatedAt:{ type: Number, default: 0 },     // unix timestamp
    // Pay_type breakdown — har to'lov turi alohida ko'rsatiladi (mobile chek formati kabi)
    // [{ pay_type_id, pay_type_name, summa, is_dollar, currency_label }]
    payments:    { type: Array,   default: () => [] },
  },
  computed: {
    // Mobil scanner uchun barcode qiymati: "<created_at>_<id>".
    // Backend GET /api/v1/service-sale/by-code/:code shu kodni tushunadi.
    barcodeValue() {
      if (!this.saleId || !this.saleCreatedAt) return '';
      return `${this.saleCreatedAt}_${this.saleId}`;
    },
    barcodeOptions() {
      return {
        format: 'CODE128',
        displayValue: true,
        fontSize: 14,
        height: 50,
        width: 1.6,
        margin: 0,
        textMargin: 2,
      };
    },
    receiptDate() {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      return `${dd}.${mm}.${yyyy}  ${hh}:${min}`;
    },
    receiptItems() {
      const showAlt = this.priceType === 'optom';
      return this.cart.map(item => {
        if (item.type === 'service') {
          const unitPrice = Number(item.labor_price || 0) + Number(item.redo_price || 0);
          return {
            name: item.name, qty: item.qty, unitPrice,
            total: unitPrice * item.qty,
            altPrice: 0,
            origPrice: 0,
          };
        } else {
          const unitPrice = Number(item.price || 0);
          const altPrice = showAlt ? Number(item.price_chakana || 0) : 0;
          const origRaw = Number(item.original_price || 0);
          // FAQAT arzon sotilganda asl narx ko'rsatiladi (chizilgan).
          // Qimmat sotilganda mijoz asl narxni bilmasligi uchun yashiramiz.
          const origPrice = (origRaw > 0 && origRaw > unitPrice) ? origRaw : 0;
          const discountPct = Number(item.discount_percent || 0);
          return {
            name: item.name, qty: item.qty, unitPrice,
            total: unitPrice * item.qty,
            altPrice,
            origPrice,
            discountPct,
          };
        }
      });
    },
    totalQty() {
      return this.receiptItems.reduce((s, it) => s + Number(it.qty || 0), 0);
    },
    // Narx tushirilmagan bolsa jami — har item asl narxida (agar orig bor) yoki sold narxida
    originalTotalSum() {
      return this.receiptItems.reduce((s, it) => {
        const base = Number(it.origPrice || 0) > 0 ? Number(it.origPrice) : Number(it.unitPrice);
        return s + base * Number(it.qty || 0);
      }, 0);
    },
    // Narx tushirish orqali chegirilgan summa
    priceReductionTotal() {
      return this.receiptItems.reduce((s, it) => {
        const orig = Number(it.origPrice || 0);
        if (orig > 0) return s + (orig - Number(it.unitPrice)) * Number(it.qty || 0);
        return s;
      }, 0);
    },
    // Chekda ko'rsatiladigan qarz: jami − to'langan
    totalDebt() {
      const total = Number(this.totalSum) || 0;
      const paid = Number(this.paidSumma) || 0;
      return Math.max(0, total - paid);
    },
    // Faqat summa > 0 bo'lgan to'lovlarni filter
    visiblePayments() {
      return (this.payments || [])
        .filter(p => Number(p.summa || 0) > 0)
        .map(p => ({
          pay_type_id:   p.pay_type_id,
          pay_type_name: p.pay_type_name || 'To\'lov',
          summa:         Number(p.summa || 0),
          is_dollar:     !!p.is_dollar,
        }));
    },
  },
  methods: {
    fmt(val) {
      return Number(val || 0).toLocaleString('uz-UZ');
    },
    // UZS suffix bilan (chekda har monetary qiymat shu helper'dan o'tadi)
    fmtUZS(val) {
      return Number(val || 0).toLocaleString('uz-UZ') + ' UZS';
    },
    fmtDate(ts) {
      if (!ts) return '';
      const d = new Date(Number(ts) * 1000);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = d.getFullYear();
      return `${dd}.${mm}.${yy}`;
    },
    // Cheknì lokal printerga yuborish — backend queue'ga qo'shadi, agent oladi
    async sendToPrinterQueue() {
      if (!this.saleId) {
        this.remotePrint = { loading: false, success: false, message: 'Avval savdoni saqlang!', jobId: null };
        return;
      }
      this.remotePrint = { loading: true, success: false, message: '', jobId: null };
      try {
        const res = await axios.post('/api/v1/print-queue/enqueue', {
          sale_id: this.saleId,
          copies: 1,
        });
        const jobId = (res.data && res.data.job_ids && res.data.job_ids[0]) || null;
        this.remotePrint = {
          loading: false, success: true, jobId,
          message: `✅ Chek navbatga qo'shildi (Job #${jobId}). 2-5 sek ichida chiqishi kerak.`,
        };
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message ? e.response.data.message : e.message;
        this.remotePrint = {
          loading: false, success: false, jobId: null,
          message: `❌ Xatolik: ${msg}`,
        };
      }
    },

    async checkJobStatus() {
      if (!this.remotePrint.jobId) return;
      try {
        const sklad_id = localStorage.getItem('sklad_id');
        const res = await axios.get(`/api/v1/print-queue/jobs?sklad_id=${sklad_id}&limit=20`);
        const job = (res.data.data || []).find(j => j.id === this.remotePrint.jobId);
        if (!job) {
          this.remotePrint.message = '⚠️ Job topilmadi (eski yoki sklad mos kelmaydi)';
          return;
        }
        const statusUz = {
          pending: '⏳ Navbatda', picked: '📦 Agent oldi', printed: '✅ Chop etildi',
          failed: '❌ Xato: ' + (job.error_message || 'noma\'lum'),
        };
        this.remotePrint.success = job.status === 'printed';
        this.remotePrint.message = `Status: ${statusUz[job.status] || job.status}`;
      } catch (e) {
        this.remotePrint.message = '❌ Tekshirishda xato: ' + e.message;
      }
    },

    printReceipt() {
      const el = document.getElementById('sale-receipt');
      const printWindow = window.open('', '_blank', 'width=420,height=700');
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Chek</title>
<style>
  @page {
    size: 80mm auto;
    margin: 0;
  }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: 80mm;
    font-family: 'Arial', 'Helvetica', sans-serif;
    font-size: 18px;
    color: #000;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding: 1.5mm 1mm;
    line-height: 1.2;
  }
  .receipt-logo {
    display: block;
    max-width: 90mm;
    max-height: 33mm;
    width: auto;
    height: auto;
    margin: 0 auto 2mm;
    object-fit: contain;
  }
  .receipt-header { text-align: center; margin-bottom: 2mm; }
  .firm-name { font-size: 24px; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; }
  .firm-phone { font-size: 17px; margin-top: 1mm; word-break: break-all; }
  .receipt-date { font-size: 17px; color: #000; margin-top: 1mm; }

  .receipt-divider { border-top: 1.5px dashed #000; margin: 2mm 0; }

  .receipt-info { margin: 1.5mm 0; }
  .info-row { display: flex; justify-content: space-between; font-size: 18px; line-height: 1.3; gap: 4px; }
  .info-label { color: #000; font-weight: 700; white-space: nowrap; }
  .info-value { font-weight: 700; text-align: right; word-break: break-word; }

  .items-section { margin: 1.5mm 0; }
  .item-block {
    padding: 1.5mm 0;
    border-top: 1px dashed #000;
  }
  .item-block:last-child { border-bottom: 1px dashed #000; }
  .ir-name {
    font-family: 'Arial Narrow', 'Arial', 'Helvetica', sans-serif;
    font-size: 13px; font-weight: 700;
    letter-spacing: -0.1px;
    word-break: break-word; white-space: normal;
    margin-bottom: 0.5mm; line-height: 1.1;
  }
  .ir-alt {
    font-size: 12px; font-style: italic; color: #000;
    margin-bottom: 0.4mm;
  }
  .ir-disc {
    display: inline-block;
    font-size: 12px; font-weight: 700; color: #000;
    border: 1.5px solid #000; padding: 0.3mm 1.2mm;
    margin: 0.4mm 0;
  }

  .leader-line {
    display: flex; align-items: baseline;
    font-size: 13px; line-height: 1.25; gap: 1px;
    width: 100%;
  }
  .ll-left { white-space: nowrap; }
  .ll-right { white-space: nowrap; font-weight: 700; text-align: right; }
  .ll-dots {
    flex: 1 1 auto;
    min-width: 4mm;
    border-bottom: 2px dotted #000;
    align-self: center;
    height: 0;
    margin: 0 1.5mm;
  }
  .ll-jami {
    font-size: 16px; font-weight: 900;
    border-top: 2px solid #000; border-bottom: 2px solid #000;
    padding: 0.8mm 0; margin: 0.8mm 0;
  }
  .ll-italic .ll-left, .ll-italic .ll-right { font-style: italic; }
  .ll-bold .ll-left, .ll-bold .ll-right { font-weight: 900; }
  /* Chizilgan asl narx — qora yupqa chiziq + italic (asosiy matndan farqlanadi, lekin printer aniq chiqaradi) */
  .ll-strike .ll-left, .ll-strike .ll-right {
    text-decoration: line-through;
    text-decoration-color: #000;
    text-decoration-thickness: 1px;
    color: #000;
    font-weight: 500;
    font-style: italic;
  }
  .ll-strike .ll-dots { border-bottom-color: #000; border-bottom-style: dotted; }

  .calc-section { margin: 1.5mm 0; }

  .debt-section { margin-top: 2mm; padding-top: 1.5mm; border-top: 1.5px dashed #000; }
  .debt-title { font-size: 19px; font-weight: 800; margin-bottom: 1mm; }
  .debt-comment-line { font-size: 16px; font-style: italic; margin-top: 1mm; word-break: break-word; }

  .receipt-footer { text-align: center; margin-top: 2mm; padding-bottom: 3mm; }
  .check-word { font-weight: 800; font-size: 18px; text-transform: uppercase; letter-spacing: 0.5px; }
  .thank-you { font-size: 17px; color: #000; margin-top: 1.5mm; font-weight: 600; }

  .receipt-barcode { text-align: center; margin-top: 2mm; padding-bottom: 3mm; }
  .receipt-barcode svg { width: 70mm; height: auto; max-height: 18mm; }
</style>
</head>
<body>${el.innerHTML}</body>
</html>`);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 400);
    },
  },
};
</script>

<style scoped>
/* === Overlay === */
.receipt-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.receipt-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-height: 95vh;
  overflow-y: auto;
}

/* === Action buttons === */
.receipt-actions {
  display: flex;
  gap: 10px;
}
.btn-print {
  background: #10b981;
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}
.btn-print-remote {
  background: linear-gradient(135deg, #6366f1, #4338ca);
  color: white;
  border: none;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
  transition: 0.15s;
}
.btn-print-remote:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.btn-print-remote:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-close-modal {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  height: 40px;
  padding: 0 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

/* === Remote print status banner === */
.remote-print-banner {
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}
.banner-ok  { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
.banner-err { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.banner-link {
  margin-left: auto;
  background: rgba(255,255,255,0.7);
  color: inherit;
  border: 1px solid currentColor;
  border-radius: 8px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.banner-link:hover { background: white; }

/* === Receipt preview (80mm simulated — 1.5x scaled) === */
.receipt {
  background: white;
  width: 340px;
  padding: 6px 4px;
  font-family: 'Arial', 'Helvetica', sans-serif;
  font-size: 16px;
  color: #000;
  border-radius: 2px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  line-height: 1.2;
}

/* Logo */
.receipt-logo {
  display: block;
  max-width: 360px;
  max-height: 138px;
  width: auto;
  height: auto;
  margin: 0 auto 8px;
  object-fit: contain;
}

/* Header */
.receipt-header { text-align: center; margin-bottom: 8px; }
.firm-name { font-size: 22px; font-weight: 900; letter-spacing: 0.5px; text-transform: uppercase; }
.firm-phone { font-size: 15px; margin-top: 3px; word-break: break-all; }
.receipt-date { font-size: 15px; color: #000; margin-top: 3px; }

/* Dividers */
.receipt-divider { border-top: 1.5px dashed #000; margin: 8px 0; }

/* Info section */
.receipt-info { margin: 6px 0; }
.info-row { display: flex; justify-content: space-between; font-size: 16px; line-height: 1.5; gap: 6px; }
.info-label { color: #000; font-weight: 700; white-space: nowrap; }
.info-value { font-weight: 700; text-align: right; word-break: break-word; }

/* Items */
.items-section { margin: 6px 0; }
.item-block {
  padding: 6px 0;
  border-top: 1px dashed #000;
}
.item-block:last-child { border-bottom: 1px dashed #000; }
.ir-name {
  /* Mahsulot nomi — Image 1 dagi kabi ixchamroq (13px), qolgan matnlar bilan teng.
     Arial Narrow kondensatsiyalangan font — kengligi ~75%, shuning uchun 80mm chekka sig'adi. */
  font-family: 'Arial Narrow', 'Arial', 'Helvetica', sans-serif;
  font-size: 13px; font-weight: 700;
  letter-spacing: -0.1px;
  line-height: 1.15;
  word-break: break-word; white-space: normal;
  margin-bottom: 2px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.ir-disc {
  display: inline-block;
  font-size: 11px; font-weight: 700;
  color: #c2410c; background: #fff7ed;
  padding: 1px 6px; border-radius: 3px;
  margin: 2px 0;
}
.ir-alt {
  font-size: 12px; font-style: italic; color: #000;
  margin-bottom: 2px;
}

/* Leader-line reusable (dotted leader between label/amount) */
.leader-line {
  display: flex; align-items: baseline;
  font-size: 13px; line-height: 1.25; gap: 1px;
  width: 100%;
}
.ll-left { white-space: nowrap; }
.ll-right { white-space: nowrap; font-weight: 700; text-align: right; }
.ll-dots {
  flex: 1 1 auto;
  min-width: 12px;
  border-bottom: 2px dotted #000;
  align-self: center;
  height: 0;
  margin: 0 4px;
}
.ll-jami {
  font-size: 16px; font-weight: 900;
  border-top: 2px solid #000; border-bottom: 2px solid #000;
  padding: 3px 0; margin: 3px 0;
}
.ll-italic .ll-left, .ll-italic .ll-right { font-style: italic; }
.ll-bold .ll-left, .ll-bold .ll-right { font-weight: 900; }
.ll-strike .ll-left, .ll-strike .ll-right {
  text-decoration: line-through;
  text-decoration-color: #000;
  text-decoration-thickness: 1px;
  color: #000;
  font-weight: 500;
  font-style: italic;
}
.ll-strike .ll-dots { border-bottom-color: #000; border-bottom-style: dotted; }

/* Calc & debt */
.calc-section { margin: 6px 0; }
.debt-section { margin-top: 8px; padding-top: 6px; border-top: 1.5px dashed #000; }
.debt-title { font-size: 17px; font-weight: 800; margin-bottom: 4px; }
.debt-comment-line { font-size: 13px; font-style: italic; margin-top: 4px; word-break: break-word; }

/* Footer */
.receipt-footer { text-align: center; margin-top: 8px; }
.check-word { font-weight: 800; font-size: 16px; text-transform: uppercase; letter-spacing: 0.5px; }
.thank-you { font-size: 15px; color: #000; margin-top: 4px; font-weight: 600; }

/* Barcode (mobil scanner uchun) */
.receipt-barcode { text-align: center; margin-top: 10px; padding-bottom: 4px; }
.receipt-barcode >>> svg { width: 280px; height: auto; max-height: 70px; }

/* === Print media === */
@media print {
  .no-print { display: none !important; }
  .receipt-overlay { position: static; background: none; }
  .receipt { width: 80mm; box-shadow: none; padding: 1.5mm 1mm; }
  .receipt-logo { max-width: 90mm; max-height: 33mm; width: auto; height: auto; }
  .receipt-barcode >>> svg { width: 70mm; max-height: 18mm; }
}
</style>
